import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

// Interfaces
export interface MemoryPlace {
  id: number;
  user_id: number;
  name: string;
  description?: string;
  properties?: Record<string, any>;
  is_root?: boolean;
  created_at: string;
  updated_at: string;
}

export interface MemoryConnection {
  id: number;
  user_id: number;
  from_place_id: number;
  to_place_id: number;
  description?: string;
  created_at: string;
  from_place_name?: string;
  to_place_name?: string;
  connected_place_id?: number;
  connected_place_name?: string;
  connected_place_description?: string;
}

export interface CreatePlaceData {
  user_id: number;
  name: string;
  description?: string;
  properties?: Record<string, any> | string;
  is_root?: boolean;
}

export interface UpdatePlaceData {
  name?: string;
  description?: string;
  properties?: Record<string, any> | string;
  is_root?: boolean;
}

export interface CreateConnectionData {
  user_id: number;
  from_place_id: number;
  to_place_id: number;
  description?: string;
}

export interface UpdateConnectionData {
  description?: string;
}

export interface PlaceConnections {
  outgoing: MemoryConnection[];
  incoming: MemoryConnection[];
  count: number;
}

class MemoryService {
  // Create a new place
  async createPlace(placeData: CreatePlaceData): Promise<{ success: boolean; id?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/memory/places', {
        method: 'POST',
        body: JSON.stringify(placeData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating place:', error);
      throw error;
    }
  }

  // Get place by ID
  async getPlaceById(placeId: number): Promise<{ success: boolean; place?: MemoryPlace; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/places/${placeId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching place:', error);
      throw error;
    }
  }

  // Get all places for a user
  async getUserPlaces(userId: number, limit?: number, offset?: number): Promise<{ success: boolean; places?: MemoryPlace[]; count?: number; message?: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/memory/places/user/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user places:', error);
      throw error;
    }
  }

  // Update a place
  async updatePlace(placeId: number, updateData: UpdatePlaceData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/places/${placeId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating place:', error);
      throw error;
    }
  }

  // Delete a place
  async deletePlace(placeId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/places/${placeId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting place:', error);
      throw error;
    }
  }

  // Create a connection between places
  async createConnection(connectionData: CreateConnectionData): Promise<{ success: boolean; id?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/memory/connections', {
        method: 'POST',
        body: JSON.stringify(connectionData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating connection:', error);
      throw error;
    }
  }

  // Get all connections for a user
  async getUserConnections(userId: number): Promise<{ success: boolean; connections?: MemoryConnection[]; count?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/connections/user/${userId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user connections:', error);
      throw error;
    }
  }

  // Get connections for a specific place
  async getPlaceConnections(placeId: number): Promise<{ success: boolean; outgoing?: MemoryConnection[]; incoming?: MemoryConnection[]; count?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/places/${placeId}/connections`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching place connections:', error);
      throw error;
    }
  }

  // Update a connection
  async updateConnection(connectionId: number, updateData: UpdateConnectionData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/connections/${connectionId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating connection:', error);
      throw error;
    }
  }

  // Delete a connection
  async deleteConnection(connectionId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/memory/connections/${connectionId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting connection:', error);
      throw error;
    }
  }
}

export const memoryService = new MemoryService();

